(() => {
  const ROOT_SELECTOR = '[data-cg-mix-root]';

  const initializeGallery = (root) => {
    const track = root.querySelector('[data-cg-mix-gallery-track]');
    const slides = [...root.querySelectorAll('[data-cg-mix-gallery-slide]')];
    const dots = [...root.querySelectorAll('[data-cg-mix-gallery-dot]')];
    const previous = root.querySelector('[data-cg-mix-gallery-previous]');
    const next = root.querySelector('[data-cg-mix-gallery-next]');

    if (!track || slides.length < 2 || !previous || !next) return;

    let frame;

    const activeIndex = () => {
      const trackLeft = track.getBoundingClientRect().left;
      return slides.reduce((closest, slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    };

    const render = () => {
      const index = activeIndex();

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      previous.disabled = index === 0;
      next.disabled = index === slides.length - 1;

      slides.forEach((slide, slideIndex) => {
        if (slideIndex === index) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    };

    const showSlide = (index) => {
      const safeIndex = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({
        left: slides[safeIndex].offsetLeft - track.offsetLeft,
        behavior: 'smooth'
      });
    };

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(render);
    }, { passive: true });

    previous.addEventListener('click', () => showSlide(activeIndex() - 1));
    next.addEventListener('click', () => showSlide(activeIndex() + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => showSlide(Number(dot.dataset.cgMixGalleryDot)));
    });

    render();
  };

  const initializeBuilder = (root) => {
    const form = root.querySelector('.cg-mix-form');
    const variantInput = root.querySelector('[data-cg-mix-variant-id]');
    const packButtons = [...root.querySelectorAll('[data-cg-mix-pack]')];
    const flavorRows = [...root.querySelectorAll('[data-cg-mix-flavor]')];
    const progress = root.querySelector('[data-cg-mix-progress]');
    const progressLabel = root.querySelector('[data-cg-mix-progress-label]');
    const progressSegments = root.querySelector('[data-cg-mix-progress-segments]');
    const liveStatus = root.querySelector('[data-cg-mix-live-status]');
    const targetCopy = root.querySelector('[data-cg-mix-target-copy]');
    const dock = root.querySelector('[data-cg-mix-dock]');
    const dockCount = root.querySelector('[data-cg-mix-dock-count]');
    const dockMeta = root.querySelector('[data-cg-mix-dock-meta]');
    const addButtons = [...root.querySelectorAll('[data-cg-mix-add]')];
    const mainAddButton = root.querySelector('[data-cg-mix-main-add]');
    const builderStart = root.querySelector('[data-cg-mix-builder-start]');
    const startLink = root.querySelector('.cg-mix__start');

    if (!form || !variantInput || !progress || !progressSegments || !addButtons.length || !builderStart) return;

    const flavors = flavorRows.map((row) => ({
      id: row.dataset.flavorId,
      key: row.dataset.flavorKey,
      name: row.dataset.flavorName,
      available: row.dataset.flavorAvailable !== 'false',
      row,
      input: row.querySelector('[data-cg-mix-property]'),
      output: row.querySelector('[data-cg-mix-count]'),
      minus: row.querySelector('[data-cg-mix-action="minus"]'),
      plus: row.querySelector('[data-cg-mix-action="plus"]')
    }));

    const initialPack = packButtons.find((button) => button.dataset.variantId === root.dataset.initialVariantId)
      || packButtons.find((button) => !button.disabled)
      || packButtons[0];

    const state = {
      target: initialPack ? Number(initialPack.dataset.bagCount) : 0,
      variantId: initialPack ? initialPack.dataset.variantId : '',
      counts: Object.fromEntries(flavors.map((flavor) => [flavor.id, 0]))
    };

    const totalSelected = () => flavors.reduce((total, flavor) => total + state.counts[flavor.id], 0);

    const fitCountsToTarget = (nextTarget) => {
      const total = totalSelected();
      if (total === 0 || nextTarget <= 0 || total <= nextTarget) return;

      const raw = flavors.map((flavor) => (state.counts[flavor.id] / total) * nextTarget);
      const scaled = raw.map(Math.floor);
      let remaining = nextTarget - scaled.reduce((sum, value) => sum + value, 0);
      const order = raw
        .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
        .sort((a, b) => b.remainder - a.remainder);

      for (let index = 0; index < remaining; index += 1) {
        scaled[order[index % order.length].index] += 1;
      }

      flavors.forEach((flavor, index) => {
        state.counts[flavor.id] = scaled[index];
      });
    };

    const renderSegments = (selected) => {
      progressSegments.style.setProperty('--segment-count', Math.max(state.target, 1));
      progressSegments.replaceChildren();

      for (let index = 0; index < state.target; index += 1) {
        const segment = document.createElement('span');
        segment.className = `cg-mix-progress__segment${index < selected ? ' is-filled' : ''}`;
        progressSegments.appendChild(segment);
      }
    };

    const selectedPack = () => packButtons.find((button) => button.dataset.variantId === state.variantId);

    const render = () => {
      flavors.forEach((flavor) => {
        if (!flavor.available) state.counts[flavor.id] = 0;
      });
      const selected = totalSelected();
      const remaining = Math.max(0, state.target - selected);
      const complete = state.target > 0 && selected === state.target;
      const pack = selectedPack();
      const available = Boolean(pack && pack.dataset.available === 'true' && !pack.disabled);
      const price = pack ? pack.dataset.price : '';
      const priceCents = pack ? Number(pack.dataset.priceCents) : 0;
      const threshold = Number(root.dataset.shippingThreshold || 0);
      const shippingText = priceCents >= threshold
        ? root.dataset.freeShippingText
        : root.dataset.shippingGoalText;

      packButtons.forEach((button) => {
        const isSelected = button.dataset.variantId === state.variantId;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });

      flavors.forEach((flavor) => {
        const count = state.counts[flavor.id];
        flavor.output.textContent = count;
        flavor.input.value = count;
        flavor.input.disabled = count === 0;
        flavor.minus.disabled = count === 0;
        flavor.plus.disabled = !flavor.available || selected >= state.target || state.target === 0;
      });

      if (targetCopy) targetCopy.textContent = state.target > 0 ? `${state.target} bags.` : 'a configured pack size.';
      if (progressLabel) progressLabel.textContent = `${selected} of ${state.target} bags selected`;
      progress.classList.toggle('is-complete', complete);
      renderSegments(selected);

      if (dockCount) dockCount.textContent = `${selected} of ${state.target} bags`;
      if (dockMeta) dockMeta.textContent = [price, shippingText].filter(Boolean).join(' \u2022 ');

      let addButtonLabel;
      if (!available && pack) {
        addButtonLabel = 'Sold out';
      } else if (complete) {
        addButtonLabel = addButtons[0].dataset.addLabel || 'Add bundle';
      } else if (state.target > 0) {
        addButtonLabel = `Select ${remaining} more`;
      } else {
        addButtonLabel = 'Select your bags';
      }

      addButtons.forEach((addButton) => {
        addButton.disabled = !complete || !available;
        addButton.setAttribute('aria-disabled', String(addButton.disabled));
        addButton.textContent = addButtonLabel;
      });

      if (liveStatus) {
        liveStatus.textContent = complete
          ? `${state.target} of ${state.target} bags selected. Bundle complete.`
          : `${selected} of ${state.target} bags selected. Select ${remaining} more.`;
      }

      variantInput.value = state.variantId;
    };

    packButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || button.dataset.variantId === state.variantId) return;
        const nextTarget = Number(button.dataset.bagCount);
        fitCountsToTarget(nextTarget);
        state.target = nextTarget;
        state.variantId = button.dataset.variantId;
        render();
      });
    });

    flavorRows.forEach((row) => {
      row.addEventListener('click', (event) => {
        const action = event.target.closest('[data-cg-mix-action]');
        if (!action || action.disabled) return;

        const flavorId = row.dataset.flavorId;
        if (!flavors.find((flavor) => flavor.id === flavorId)?.available) return;
        if (action.dataset.cgMixAction === 'plus' && totalSelected() < state.target) {
          state.counts[flavorId] += 1;
        }
        if (action.dataset.cgMixAction === 'minus' && state.counts[flavorId] > 0) {
          state.counts[flavorId] -= 1;
        }
        render();
      });
    });

    form.addEventListener('submit', (event) => {
      const pack = selectedPack();
      const available = Boolean(pack && pack.dataset.available === 'true' && !pack.disabled);
      const hasUnavailableFlavor = flavors.some((flavor) => !flavor.available && state.counts[flavor.id] > 0);
      if (!available || hasUnavailableFlavor || state.target <= 0 || totalSelected() !== state.target) {
        event.preventDefault();
        builderStart.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      flavors.forEach((flavor) => {
        const count = state.counts[flavor.id];
        flavor.input.value = count;
        flavor.input.disabled = !flavor.available || count === 0;
      });
    });

    if (startLink) {
      startLink.addEventListener('click', (event) => {
        event.preventDefault();
        builderStart.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    let dockFrame;
    const renderDock = () => {
      if (!dock) return;

      const mainAddRect = mainAddButton ? mainAddButton.getBoundingClientRect() : null;
      const header = document.querySelector('[data-cg-v2-header], .cg-v2-header, header');
      const headerRect = header ? header.getBoundingClientRect() : null;
      const activationLine = Math.max(0, headerRect?.bottom || 0) + 8;
      const selectorPassed = Boolean(mainAddRect && mainAddRect.bottom <= activationLine);
      const footer = document.querySelector('[data-cg-site-footer], footer, .shopify-section-group-footer-group');
      const footerVisible = Boolean(footer && footer.getBoundingClientRect().top < window.innerHeight - 10);

      root.classList.toggle('is-builder-active', selectorPassed);
      root.classList.toggle('is-footer-hidden', footerVisible);
    };

    const requestDockRender = () => {
      cancelAnimationFrame(dockFrame);
      dockFrame = requestAnimationFrame(renderDock);
    };

    if (dock) {
      window.addEventListener('scroll', requestDockRender, { passive: true });
      window.addEventListener('resize', requestDockRender);
    }

    if (initialPack) {
      state.target = Number(initialPack.dataset.bagCount);
      state.variantId = initialPack.dataset.variantId;
    }

    render();
    if (dock) renderDock();
  };

  const initialize = (root) => {
    if (!root || root.dataset.cgMixInitialized === 'true') return;
    root.dataset.cgMixInitialized = 'true';
    initializeGallery(root);
    initializeBuilder(root);
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
